import type { FiveSSlice } from '../bs/fiveS'

/**
 * English for the fiveS screens.
 *
 * Complete, not partial — see en/index.ts. "Walk" for *obilazak*, because that
 * is what a 5S evaluation is: somebody walking a machine with a checklist. The
 * counted phrases keep the count after a colon, the same shape as the base —
 * English would not need it, but a running total that changes wording as it
 * crosses one reads worse, not better.
 */
export const fiveS: FiveSSlice = {
  'fiveS.title': '5S evaluation',
  'fiveS.subtitle':
    'The form and its scale follow the type of work centre — both load as soon as you pick one.',
  'fiveS.denied.title': 'You may not enter a 5S evaluation',
  'fiveS.denied.description':
    '5S scores are entered by supervisors, by the committee and by the administrator.',

  'fiveS.help.oneWalk':
    'One walk per machine, per day, per shift. A morning walk and a night walk of the same machine on the same day are two separate records, because they describe two different states of the centre.',

  'fiveS.workCenter': 'Work centre',
  'fiveS.workCenter.placeholder': 'Choose',
  'fiveS.date': 'Date of the walk',
  'fiveS.date.help':
    'You record the day you actually walked the machine, not the day you type the scores in — the record describes the state of the centre on that day.',
  'fiveS.periodHint': 'This evaluation counts towards {month} {year}.',
  'fiveS.shift': 'Shift',
  'fiveS.list.allShifts': 'All shifts',
  'fiveS.shift.hint': 'Which shift was keeping the centre. Optional.',
  'fiveS.shift.help':
    'The application suggests whichever shift is running at this moment; if the plant does not work in shifts, this field is not there at all.',
  'fiveS.shift.none': 'No shift',

  'fiveS.workers': 'Workers at the centre',
  'fiveS.workers.hint': 'Who kept the centre over this period. Not required.',
  'fiveS.workers.help':
    'The per-worker 5S figure is calculated from this list, and it describes the state of the machine they kept rather than the people themselves.',

  'fiveS.pickCenter.title': 'Choose a work centre',
  'fiveS.pickCenter.description':
    'The form and the scoring scale depend on the type of centre, so the criteria appear only once you have chosen one.',
  'fiveS.template.loading': 'Loading the form…',
  'fiveS.template.missing': 'No form found',

  'fiveS.onlyUnanswered': 'Show only unscored ({count})',
  'fiveS.onlyUnanswered.hint':
    'Useful towards the end of the walk, so you do not hunt through the whole list for what you missed.',

  'fiveS.comment': 'Note',
  'fiveS.comment.hint': 'Management sees this alongside the score.',
  'fiveS.comment.placeholder':
    'e.g. Tools were put back in place, but the zone marking is not finished.',

  'fiveS.remaining': 'Criteria left: {count}',
  'fiveS.allScored': 'Every criterion scored',
  'fiveS.progressLabel': 'Criteria scored',
  'fiveS.percentage.help':
    'A percentage is used because work centres can be scored on different scales, so the raw totals cannot be compared.',

  'fiveS.saveDraft': 'Save draft',
  'fiveS.submit': 'Submit',
  'fiveS.submit.hint':
    'You can only submit once every criterion is scored. A draft can be saved at any time.',
  'fiveS.submit.help':
    'A submitted evaluation is never changed, because it is the record of the machine on that day; only an administrator can reopen one.',
  'fiveS.submit.blocked': 'Score every criterion before submitting. Still unscored: {count}.',
  'fiveS.draftSaved': 'Draft saved.',
  'fiveS.submitted': 'Evaluation submitted — {score}.',
  'fiveS.submitted.next': 'Evaluation submitted — {score}. Choose the next work centre.',
  'fiveS.submitted.open': 'Open',
  'fiveS.saveFailed': 'That could not be saved.',

  'fiveS.centerChange.title': 'Change the work centre?',
  'fiveS.centerChange.stay': 'Stay here',
  'fiveS.centerChange.confirm': 'Change and delete the scores',
  'fiveS.centerChange.body':
    'Every work centre has its own form, so the scores you have entered cannot carry over. Scores entered: {count}. Changing the work centre deletes all of them.',
  'fiveS.centerChange.keepHint':
    'If you want to keep them, close this window first and press “Save draft”.',

  'fiveS.stickers.title': 'QR stickers',
  'fiveS.stickers.subtitle':
    'Stickers for the machines. A worker scans the code and the report opens with the work centre already chosen.',
  'fiveS.stickers.printHint':
    'Print on A4, cut along the dashed line and laminate. Stick them up at eye level, away from dust and oil.',
  'fiveS.stickers.printAll': 'Print all',
  'fiveS.stickers.printSelected': 'Print ({count})',
  'fiveS.stickers.clearSelection': 'Clear the selection',
  'fiveS.stickers.centers': 'Work centres ({count})',
  'fiveS.stickers.select': 'Select {name}',
  'fiveS.stickers.hasCode': 'Issued',
  'fiveS.stickers.noCode': 'No code',
  'fiveS.stickers.regenerate': 'Reissue',
  'fiveS.stickers.regenerateFor': 'Reissue the QR code for {name}',
  'fiveS.stickers.regenerateConfirm':
    'Reissue the QR code for “{name}”?\n\nThe sticker currently on the machine stops working straight away. You will have to print a new one and stick it up.',
  'fiveS.stickers.regenerated': 'The QR code has been reissued.',
  'fiveS.stickers.regenerateFailed': 'That could not be reissued.',

  'fiveS.badge.preparing': 'Preparing the card…',
  'fiveS.badge.alt': 'QR card for signing in',
  'fiveS.badge.warning':
    'The card is a key — whoever holds it can sign in as this account, and a photograph of it works just as well. Look after it like an ID card. If it is lost, issue a new one; the old one stops working at once.',
  'header.newEvaluation': 'New 5S evaluation',
  'fiveS.list.title': '5S evaluations',
  'fiveS.list.subtitle': 'Every 5S walk that has been done, most recent first.',
  'fiveS.list.readOnly': '5S evaluations are carried out by supervisors — you can read them here.',
  'fiveS.list.search': 'Search by work centre or evaluator…',
  'fiveS.list.allCentres': 'All work centres',
  'fiveS.list.allStatuses': 'All statuses',
  'fiveS.list.status': 'Status',
  'fiveS.list.empty': 'No 5S evaluations yet.',
  'fiveS.list.workers': '{count} workers',
  'fiveS.status.draft': 'Draft',
  'fiveS.status.submitted': 'Submitted',
  'fiveS.detail.back': 'Back to evaluations',
  'fiveS.detail.notScored': 'not scored',
  'fiveS.detail.edit': 'Edit',

  'fiveS.insights.categories.title': 'The five S by category',
  'fiveS.insights.categories.hint': 'The filled shape is this walk, the outline is the one before it.',
  'fiveS.insights.categories.hintFirst': 'The first walk of this centre — nothing to compare it against yet.',
  'fiveS.insights.history.title': 'How this work centre is moving',
  'fiveS.insights.history.hint': 'The last twelve walks, oldest first.',
  'fiveS.insights.history.tooShort': 'One more walk is needed before there is a direction.',
  'fiveS.insights.criteria.title': 'What to fix first',
  'fiveS.insights.criteria.hint': 'Criteria worst first, rather than in checklist order.',
  'fiveS.insights.empty': 'Nothing was scored on this walk.',
  'fiveS.insights.series.current': 'This walk',
  'fiveS.insights.series.previous': 'Previous walk',
  'fiveS.insights.series.score': 'Score',
  'fiveS.insights.delta.same': 'unchanged',
  'fiveS.insights.rank.title': 'Position in the plant this month',
  'fiveS.insights.rank.value': '{position} of {of}',
  'fiveS.insights.rank.plantAverage': 'Plant average',
  'fiveS.insights.rank.hint': 'Difference from the average: {difference}.',

}
